/**
 * Canonical server state. SPEC.md §36, §37, §15.
 *
 * The only ways state gets in here are applySnapshot() and one apply function
 * per sequenced server event. Nothing in this file computes a score: totals
 * arrive already derived from the transfer ledger, because the client deriving
 * its own would be a second implementation of the zero-sum rule waiting to
 * drift. §15.1, §25, §36
 *
 * Ephemeral UI (open sheets, the keypad draft, the sticky mode, toasts) lives in
 * ui-store. §37
 */

import { create } from 'zustand';
import {
  SNAPSHOT_RECENT_TRANSFER_LIMIT,
  canPlayerEnterHand,
  dealerPlayerId as dealerOfHand,
  entryOf,
  inferWinner,
  resolveHand,
  silentPlayers,
  type Adjustment,
  type ClientPermissions,
  type DrinkRecordedEvent,
  type HandEntry,
  type HandEntryChangedEvent,
  type PlayerConnectionChangedEvent,
  type PlayerJoinedEvent,
  type PlayerRemovedEvent,
  type PlayerSeatChangedEvent,
  type PlayerUpdatedEvent,
  type RoundDealerChangedEvent,
  type PlayerView,
  type Round,
  type RoundCompletedEvent,
  type RoundStartedEvent,
  type RoundSummary,
  type RoundVoidedEvent,
  type Session,
  type SeatAssignment,
  type SessionSnapshot,
  type SessionUpdatedEvent,
  type Transfer,
} from '@card-counter/shared';

export interface SessionStoreState {
  session: Session | null;
  players: PlayerView[];
  activeRound: Round | null;
  /**
   * The summary from the most recent round:completed. The completion sheet is
   * opened by that event and has nowhere else to read it from. §13.2, §51
   */
  lastRoundSummary: RoundSummary | null;
  /**
   * What each player has reported losing this hand. Nothing is derived from a
   * score here — this is the input the winner is read back out of. §3.1
   */
  handEntries: HandEntry[];
  /** Ascending by sequence, capped at the snapshot's own limit. §15.5 */
  recentTransfers: Transfer[];
  /**
   * The live hand's one-ended rows, which only drinking mode produces. Read by
   * the history drawer and never added up here: every total in this store
   * arrives already derived by the server. §4.6, §36
   */
  handAdjustments: Adjustment[];
  totalsByPlayerId: Record<string, number>;
  roundTotalsByPlayerId: Record<string, number>;
  positiveRoundTotal: number;
  negativeRoundTotal: number;
  /** Computed server-side; null until the first snapshot lands. §27 */
  permissions: ClientPermissions | null;
  /** Highest sequence actually applied. §15.2 */
  sequence: number;
}

export interface SessionStoreActions {
  applySnapshot(snapshot: SessionSnapshot): void;
  applySessionUpdated(event: SessionUpdatedEvent): void;
  applyPlayerJoined(event: PlayerJoinedEvent): void;
  applyPlayerUpdated(event: PlayerUpdatedEvent): void;
  applyPlayerSeatChanged(event: PlayerSeatChangedEvent): void;
  applyPlayerRemoved(event: PlayerRemovedEvent): void;
  /** Ephemeral and unsequenced, by design. §8.5 */
  applyPlayerConnectionChanged(event: PlayerConnectionChangedEvent): void;
  applyRoundStarted(event: RoundStartedEvent): void;
  applyRoundCompleted(event: RoundCompletedEvent): void;
  /** One player's reported loss was set, amended or withdrawn. §3.1 */
  applyHandEntryChanged(event: HandEntryChangedEvent): void;
  /** A player finished their drink, which is drinking mode's only credit. §4.6 */
  applyDrinkRecorded(event: DrinkRecordedEvent): void;
  /** The host moved the button. One field changes; nothing else does. §9.6 */
  applyRoundDealerChanged(event: RoundDealerChangedEvent): void;
  /** The host deleted a hand: its points are cancelled and its reports gone. §12.5 */
  applyRoundVoided(event: RoundVoidedEvent): void;
  reset(): void;
}

export type SessionStore = SessionStoreState & SessionStoreActions;

const EMPTY_STATE: SessionStoreState = {
  session: null,
  players: [],
  activeRound: null,
  lastRoundSummary: null,
  handEntries: [],
  recentTransfers: [],
  handAdjustments: [],
  totalsByPlayerId: {},
  roundTotalsByPlayerId: {},
  positiveRoundTotal: 0,
  negativeRoundTotal: 0,
  permissions: null,
  sequence: 0,
};

/**
 * Contiguity is the transport's problem — it detects gaps and resyncs (§15.2).
 * The store's only job is never to regress, so a duplicate or out-of-order
 * delivery is dropped instead of rewinding state.
 */
function isStale(state: SessionStoreState, sequence: number): boolean {
  return sequence <= state.sequence;
}

function withPlayer(players: PlayerView[], next: PlayerView): PlayerView[] {
  const index = players.findIndex((p) => p.id === next.id);
  if (index === -1) return [...players, next];
  const copy = [...players];
  copy[index] = next;
  return copy;
}

function appendTransfer(existing: Transfer[], transfer: Transfer): Transfer[] {
  if (existing.some((t) => t.id === transfer.id)) return existing;
  const next = [...existing, transfer];
  return next.length > SNAPSHOT_RECENT_TRANSFER_LIMIT
    ? next.slice(next.length - SNAPSHOT_RECENT_TRANSFER_LIMIT)
    : next;
}

function appendTransfers(existing: Transfer[], incoming: readonly Transfer[]): Transfer[] {
  let next = existing;
  for (const transfer of incoming) next = appendTransfer(next, transfer);
  return next;
}

/**
 * The live hand's one-ended rows after a change. §4.6
 *
 * Uncapped, unlike the transfer tail: a hand has at most a handful of them, and
 * they are cleared with the hand rather than accumulating over a night.
 */
function appendAdjustments(
  existing: Adjustment[],
  incoming: readonly Adjustment[],
): Adjustment[] {
  const known = new Set(existing.map((a) => a.id));
  const fresh = incoming.filter((a) => !known.has(a.id));
  return fresh.length === 0 ? existing : [...existing, ...fresh];
}

function zeroed(playerIds: readonly string[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const id of playerIds) totals[id] = 0;
  return totals;
}

export const useSessionStore = create<SessionStore>()((set) => ({
  ...EMPTY_STATE,

  applySnapshot: (snapshot) =>
    set({
      session: snapshot.session,
      players: [...snapshot.players],
      activeRound: snapshot.activeRound,
      handEntries: [...snapshot.handEntries],
      // Normalised on the way in so undo eligibility and history never depend on
      // the order the server happened to serialise. §15.2
      recentTransfers: [...snapshot.recentTransfers].sort((a, b) => a.sequence - b.sequence),
      handAdjustments: [...snapshot.handAdjustments],
      totalsByPlayerId: { ...snapshot.totalsByPlayerId },
      roundTotalsByPlayerId: { ...snapshot.roundTotalsByPlayerId },
      positiveRoundTotal: snapshot.positiveRoundTotal,
      negativeRoundTotal: snapshot.negativeRoundTotal,
      permissions: snapshot.permissions,
      sequence: snapshot.sequence,
    }),

  applySessionUpdated: (event) =>
    set((state) =>
      isStale(state, event.sequence)
        ? state
        : { session: event.session, sequence: event.sequence },
    ),

  applyPlayerJoined: (event) =>
    set((state) => {
      if (isStale(state, event.sequence)) return state;
      const players = withPlayer(state.players, event.player);
      return {
        players,
        // A new seat starts level rather than absent, so the ring never renders
        // a blank score. §25
        totalsByPlayerId: { [event.player.id]: 0, ...state.totalsByPlayerId },
        roundTotalsByPlayerId: { [event.player.id]: 0, ...state.roundTotalsByPlayerId },
        sequence: event.sequence,
      };
    }),

  applyPlayerUpdated: (event) =>
    set((state) =>
      isStale(state, event.sequence)
        ? state
        : { players: withPlayer(state.players, event.player), sequence: event.sequence },
    ),

  applyPlayerSeatChanged: (event) =>
    set((state) => {
      if (isStale(state, event.sequence)) return state;
      // Only the seats that moved are broadcast. §9.4
      const bySeat = new Map(event.seats.map((s) => [s.playerId, s.seatIndex]));
      return {
        players: state.players.map((p) => {
          const seatIndex = bySeat.get(p.id);
          return seatIndex === undefined ? p : { ...p, seatIndex };
        }),
        sequence: event.sequence,
      };
    }),

  applyPlayerRemoved: (event) =>
    set((state) => {
      if (isStale(state, event.sequence)) return state;
      // Flagged, never dropped: their transfers stay in history and in the final
      // accounting. §31
      return {
        players: state.players.map((p) =>
          p.id === event.playerId ? { ...p, isRemoved: true } : p,
        ),
        sequence: event.sequence,
      };
    }),

  applyPlayerConnectionChanged: (event) =>
    set((state) => ({
      players: state.players.map((p) =>
        p.id === event.playerId
          ? { ...p, presence: event.presence, lastSeenAt: event.lastSeenAt }
          : p,
      ),
    })),

  applyRoundStarted: (event) =>
    set((state) => {
      if (isStale(state, event.sequence)) return state;
      const ids = state.players.map((p) => p.id);
      return {
        activeRound: event.round,
        /**
         * The hand that just finished deliberately survives the next deal. §7.3
         *
         * It used to be cleared here, because a hand only ended when somebody
         * opened the next one and the two were seconds apart. Now they are the
         * same transaction: clearing it would wipe the summary in the same
         * frame it arrived, so the table would never be told which hand it just
         * played or who took it. §4.3, §13.1
         */
        // A new hand has been told nothing yet. §3.1
        handEntries: [],
        handAdjustments: [],
        roundTotalsByPlayerId: zeroed(ids),
        positiveRoundTotal: 0,
        negativeRoundTotal: 0,
        sequence: event.sequence,
      };
    }),

  applyRoundCompleted: (event) =>
    set((state) => {
      if (isStale(state, event.sequence)) return state;
      return {
        activeRound: null,
        lastRoundSummary: event.summary,
        handEntries: [],
        handAdjustments: [],
        // The centre keeps showing the hand that just finished until the next one
        // starts, straight from the server's summary. §7.3, §13.2
        roundTotalsByPlayerId: { ...event.summary.netByPlayerId },
        positiveRoundTotal: event.summary.positiveTotal,
        negativeRoundTotal: event.summary.negativeTotal,
        sequence: event.sequence,
      };
    }),

  applyHandEntryChanged: (event) =>
    set((state) => {
      if (isStale(state, event.sequence)) return state;
      /**
       * One report can move a great deal more than one row — the report that
       * leaves one player silent settles everybody's losses at once, and a late
       * one can cancel them all again — and every row shares the span this event
       * covers, because one reported loss is one mutation however many transfers
       * it turns out to imply. §15.2, §3.1
       *
       * `event.reversedTransferIds` is not indexed here: every inverse row in
       * `event.transfers` already carries `reversesTransferId`, which is what
       * liveTransfers() and isReversed() read. §12.2
       */
      return {
        // Replaced wholesale rather than merged: the payload is the hand as it
        // now stands, and a withdrawal is a report that is no longer in it. §3.1
        handEntries: [...event.entries],
        recentTransfers: appendTransfers(state.recentTransfers, event.transfers),
        handAdjustments: appendAdjustments(state.handAdjustments, event.adjustments),
        // Straight from the payload. The client never adds up a fan-out itself. §36
        totalsByPlayerId: { ...event.totalsByPlayerId },
        roundTotalsByPlayerId: { ...event.roundTotalsByPlayerId },
        positiveRoundTotal: event.positiveRoundTotal,
        negativeRoundTotal: event.negativeRoundTotal,
        sequence: event.sequence,
      };
    }),

  applyDrinkRecorded: (event) =>
    set((state) => {
      if (isStale(state, event.sequence)) return state;
      /**
       * One row, and the totals it produced. Nothing here adds the cup's price
       * to anybody: what a drink is worth is the server's to know, and a client
       * that computed it would be a second opinion about the exchange rate the
       * whole night is priced in. §4.6, §36
       */
      return {
        handAdjustments: appendAdjustments(state.handAdjustments, [event.adjustment]),
        totalsByPlayerId: { ...event.totalsByPlayerId },
        roundTotalsByPlayerId: { ...event.roundTotalsByPlayerId },
        positiveRoundTotal: event.positiveRoundTotal,
        negativeRoundTotal: event.negativeRoundTotal,
        sequence: event.sequence,
      };
    }),

  /**
   * The host re-anchored the deal. §9.6
   *
   * Only the *active* round is patched, and only when the event is about it: a
   * frame for some other hand would otherwise overwrite the live one wholesale.
   * Nothing here works out who is now dealing — `dealerPlayerId` derives that
   * from the offset on demand, which is what keeps one rule rather than two. §60
   */
  applyRoundDealerChanged: (event) =>
    set((state) => {
      if (isStale(state, event.sequence)) return state;
      // The sequence is applied either way: the frame was delivered and counted,
      // and refusing to advance it would read as a gap on the next one. §15.2
      if (state.activeRound?.id !== event.round.id) {
        return { sequence: event.sequence };
      }
      return { activeRound: event.round, sequence: event.sequence };
    }),

  applyRoundVoided: (event) =>
    set((state) => {
      if (isStale(state, event.sequence)) return state;
      /**
       * Deleting a hand is a correction like any other: inverses arrive as
       * transfers and the totals arrive already derived, so nothing here
       * subtracts anything. What is unusual is the round it lands on — emptying
       * the *live* hand clears its reports in place, while voiding a closed one
       * leaves the live hand alone — so the payload states both rather than
       * making this work them out. §12.5, §36
       */
      return {
        activeRound: event.activeRound,
        handEntries: [...event.handEntries],
        recentTransfers: appendTransfers(state.recentTransfers, event.transfers),
        // Replaced rather than appended: the payload states the live hand's
        // rows afterwards, and deleting the open hand is precisely the case
        // where what it had is gone. §12.5
        handAdjustments: [...event.handAdjustments],
        // The deleted hand's summary described points that no longer exist, so
        // showing it as the last hand played would be a lie. §13.1
        lastRoundSummary:
          state.lastRoundSummary?.round.id === event.round.id ? null : state.lastRoundSummary,
        totalsByPlayerId: { ...event.totalsByPlayerId },
        roundTotalsByPlayerId: { ...event.roundTotalsByPlayerId },
        positiveRoundTotal: event.positiveRoundTotal,
        negativeRoundTotal: event.negativeRoundTotal,
        sequence: event.sequence,
      };
    }),

  reset: () => set({ ...EMPTY_STATE }),
}));

// --- Selectors ---------------------------------------------------------------
//
// Derived arrays are memoised on the identity of the array they derive from.
// Returning a fresh array on every read would make useSessionStore(selector)
// re-render forever, and re-sorting twelve players on every keystroke is exactly
// the wasted work §44 warns about.

const activeSeatCache = new WeakMap<readonly PlayerView[], PlayerView[]>();
const allSeatCache = new WeakMap<readonly PlayerView[], PlayerView[]>();
const seatAssignmentCache = new WeakMap<readonly PlayerView[], SeatAssignment[]>();
const newestFirstCache = new WeakMap<readonly Transfer[], Transfer[]>();
const departedCache = new WeakMap<readonly PlayerView[], string[]>();

function bySeatIndex(a: PlayerView, b: PlayerView): number {
  return a.seatIndex - b.seatIndex;
}

/** Players at the live table, in canonical clockwise seat order. §9.1, §31 */
export function playersBySeat(state: SessionStoreState): PlayerView[] {
  const cached = activeSeatCache.get(state.players);
  if (cached) return cached;
  const sorted = state.players.filter((p) => !p.isRemoved).sort(bySeatIndex);
  activeSeatCache.set(state.players, sorted);
  return sorted;
}

/** Includes removed players, for final accounting and history. §31, §52 */
export function allPlayersBySeat(state: SessionStoreState): PlayerView[] {
  const cached = allSeatCache.get(state.players);
  if (cached) return cached;
  const sorted = [...state.players].sort(bySeatIndex);
  allSeatCache.set(state.players, sorted);
  return sorted;
}

/** Newest first, for the history drawer. §13.1 */
export function recentTransfersNewestFirst(state: SessionStoreState): Transfer[] {
  const cached = newestFirstCache.get(state.recentTransfers);
  if (cached) return cached;
  const ordered = [...state.recentTransfers].reverse();
  newestFirstCache.set(state.recentTransfers, ordered);
  return ordered;
}

/**
 * Whether a snapshot has ever been applied. Deliberately not `sequence > 0`: a
 * table that nobody has joined or scored in yet sits at sequence 0 legitimately,
 * and treating that as "no state" leaves the host who just created it staring at
 * a spinner. Both the readiness gate and the transport's contiguity check hang
 * off this. §15.2, §16.3
 */
export function hasSnapshot(state: SessionStoreState): boolean {
  return state.session !== null && state.permissions !== null;
}

/**
 * Players the host has taken off the table. §31, §3.1
 *
 * The hand still pays whoever it was dealt to, but it stops waiting for anyone
 * in here — so every derivation that asks "who is still silent" needs it, and
 * every one of them is the shared function the server derives the ledger with.
 * Memoised on the player array so a selector never returns a fresh one. §44, §60
 */
export function departedPlayerIds(state: SessionStoreState): string[] {
  const cached = departedCache.get(state.players);
  if (cached) return cached;
  const departed = state.players.filter((p) => p.isRemoved).map((p) => p.id);
  departedCache.set(state.players, departed);
  return departed;
}

export function playerById(state: SessionStoreState, playerId: string | null): PlayerView | null {
  if (!playerId) return null;
  return state.players.find((p) => p.id === playerId) ?? null;
}

/** Game total. Absent means level, never blank. §25 */
export function totalFor(state: SessionStoreState, playerId: string): number {
  return state.totalsByPlayerId[playerId] ?? 0;
}

export function roundTotalFor(state: SessionStoreState, playerId: string): number {
  return state.roundTotalsByPlayerId[playerId] ?? 0;
}

/** The viewing player. Identity comes from server-computed permissions. §27 */
export function me(state: SessionStoreState): PlayerView | null {
  return playerById(state, state.permissions?.playerId ?? null);
}

export function myPlayerId(state: SessionStoreState): string | null {
  return state.permissions?.playerId ?? null;
}

export function isHost(state: SessionStoreState): boolean {
  return state.permissions?.isHost ?? false;
}

export function isRoundActive(state: SessionStoreState): boolean {
  return state.activeRound !== null && state.activeRound.status === 'active';
}

/**
 * Whether a player may report a loss this hand, per the shared rule. §11
 *
 * Sequenced events carry no refreshed ClientPermissions, so between snapshots the
 * display state is re-derived here from shared domain functions. The server still
 * re-checks every mutation — this only decides what looks enabled. §27
 */
export function canEnterAs(state: SessionStoreState, playerId: string | null): boolean {
  return canPlayerEnterHand({
    participantIds: state.activeRound?.participantIds ?? [],
    playerId,
    roundActive: isRoundActive(state),
  });
}

/**
 * The player this hand is going to, or null while it cannot be told. §3.1
 *
 * Derived on every client from the same shared function the server derives the
 * ledger from, so "Mike wins this hand" and the rows that pay him can never be
 * two different opinions. §60
 */
export function winnerPlayerId(state: SessionStoreState): string | null {
  const round = state.activeRound;
  if (!round) return null;
  return inferWinner(state.handEntries, round.participantIds, departedPlayerIds(state));
}

/** Who is at the table and where, in the shape the shared seating rules take. §9.1 */
function seatAssignments(state: SessionStoreState): SeatAssignment[] {
  const cached = seatAssignmentCache.get(state.players);
  if (cached) return cached;
  const seats = playersBySeat(state).map((player) => ({
    playerId: player.id,
    seatIndex: player.seatIndex,
  }));
  seatAssignmentCache.set(state.players, seats);
  return seats;
}

/**
 * Whoever is dealing the hand on screen, or null before the first deal. §9.5
 *
 * Derived on every device from the same shared rule, so the deal cannot pass
 * clockwise on one phone and anticlockwise on another. The hand it follows is the
 * one the screen is showing: the live hand, or — in the moment between a
 * deliberate close and the next deal — the one just played, so the marker does
 * not blink off a table that is still looking at it. §7.3, §60
 */
export function dealerPlayerId(state: SessionStoreState): string | null {
  const round = state.activeRound ?? state.lastRoundSummary?.round ?? null;
  if (!round) return null;
  return dealerOfHand({
    handNumber: round.number,
    seats: seatAssignments(state),
    // Whoever the hand was dealt between, minus anyone since removed. §3.3, §31
    participantIds: round.participantIds,
    // Where the host moved the button to, if they have. Zero otherwise, which is
    // the plain clockwise rotation. §9.6
    dealerOffset: round.dealerOffset,
  });
}

/** Participants who have reported nothing yet. The winner is the last one. §3.1 */
export function silentPlayerIds(state: SessionStoreState): string[] {
  const round = state.activeRound;
  if (!round) return [];
  return silentPlayers(state.handEntries, round.participantIds, departedPlayerIds(state));
}

/**
 * Whether every reported loss has actually moved points, and how many are still
 * waiting. This is what the score screen has to be able to say out loud: a
 * player who reported and sees nobody's score move needs to know the table is
 * waiting on someone rather than that their tap was dropped. §3.1, §14.1
 */
export function handProgress(state: SessionStoreState): {
  reportedCount: number;
  silentCount: number;
  needsWinner: boolean;
  winnerPlayerId: string | null;
  settled: boolean;
} {
  const round = state.activeRound;
  if (!round) {
    return {
      reportedCount: 0,
      silentCount: 0,
      needsWinner: false,
      winnerPlayerId: null,
      settled: true,
    };
  }
  const resolved = resolveHand({
    entries: state.handEntries,
    participantIds: round.participantIds,
    departedPlayerIds: departedPlayerIds(state),
    // The client re-derives with the same rule the server writes the ledger
    // with, so it has to be told the same thing about the table. §60, §4.6
    drinkingMode: state.session?.drinkingMode ?? false,
  });
  return {
    reportedCount: state.handEntries.length,
    silentCount: resolved.silentPlayerIds.length,
    needsWinner: state.handEntries.some((entry) => entry.shape === 'TO_WINNER'),
    winnerPlayerId: resolved.winnerPlayerId,
    settled: resolved.settled,
  };
}

// --- Hooks -------------------------------------------------------------------

export const usePlayersBySeat = (): PlayerView[] => useSessionStore(playersBySeat);
export const useAllPlayersBySeat = (): PlayerView[] => useSessionStore(allPlayersBySeat);
export const useRecentTransfers = (): Transfer[] => useSessionStore(recentTransfersNewestFirst);
export const useMe = (): PlayerView | null => useSessionStore(me);
export const useMyPlayerId = (): string | null => useSessionStore(myPlayerId);
export const useIsHost = (): boolean => useSessionStore(isHost);
export const useIsRoundActive = (): boolean => useSessionStore(isRoundActive);
export const useActiveRound = (): Round | null => useSessionStore((s) => s.activeRound);
export const useSessionInfo = (): Session | null => useSessionStore((s) => s.session);
export const useLastRoundSummary = (): RoundSummary | null =>
  useSessionStore((s) => s.lastRoundSummary);

export const useTotalFor = (playerId: string): number =>
  useSessionStore((s) => totalFor(s, playerId));

export const useRoundTotalFor = (playerId: string): number =>
  useSessionStore((s) => roundTotalFor(s, playerId));

export const usePlayerById = (playerId: string | null): PlayerView | null =>
  useSessionStore((s) => playerById(s, playerId));

export const useCanEnterAs = (playerId: string | null): boolean =>
  useSessionStore((s) => canEnterAs(s, playerId));

/** Every report standing for the live hand. §3.1 */
export const useHandEntries = (): HandEntry[] => useSessionStore((s) => s.handEntries);

/** One player's standing report for the live hand, or null. §12.2 */
export const useHandEntryOf = (playerId: string | null): HandEntry | null =>
  useSessionStore((s) => entryOf(s.handEntries, playerId));

export const useWinnerPlayerId = (): string | null => useSessionStore(winnerPlayerId);

/**
 * Whether this player is dealing the hand on screen. §9.5
 *
 * A boolean rather than the dealer's id, so a seat only re-renders when the deal
 * actually passes to or from it. §44
 */
export const useIsDealer = (playerId: string): boolean =>
  useSessionStore((s) => dealerPlayerId(s) === playerId);

export const useHandProgress = (): ReturnType<typeof handProgress> => {
  // Selected field by field: handProgress builds a fresh object, and returning
  // one from a selector would re-render on every unrelated store write. §44
  const reportedCount = useSessionStore((s) => s.handEntries.length);
  const silentCount = useSessionStore((s) => silentPlayerIds(s).length);
  const needsWinner = useSessionStore((s) =>
    s.handEntries.some((entry) => entry.shape === 'TO_WINNER'),
  );
  const winner = useSessionStore(winnerPlayerId);
  return {
    reportedCount,
    silentCount,
    needsWinner,
    winnerPlayerId: winner,
    settled: !needsWinner || winner !== null,
  };
};

/** Ascending by sequence, and the input to every shared entry helper. §12.2 */
export const useRecentTransfersAscending = (): Transfer[] =>
  useSessionStore((s) => s.recentTransfers);

/** Everyone still at the table, which sets a lone loser's fan-out width. §2.1 */
export const useActivePlayerCount = (): number => useSessionStore((s) => playersBySeat(s).length);

/** Round balance readout, still used by the ended-game screen. §14.1 */
export const useRoundBalance = (): { positive: number; negative: number } => {
  const positive = useSessionStore((s) => s.positiveRoundTotal);
  const negative = useSessionStore((s) => s.negativeRoundTotal);
  return { positive, negative };
};

/**
 * Whether this table is playing the variant where the points do not balance,
 * and what a cup is worth in it. §4.6
 *
 * A boolean and a number rather than the session object, so a component that
 * only cares about the mode does not re-render every time the sequence moves. §44
 */
export const useDrinkingMode = (): boolean =>
  useSessionStore((s) => s.session?.drinkingMode ?? false);

export const usePointsPerDrink = (): number | null =>
  useSessionStore((s) => s.session?.pointsPerDrink ?? null);

/** The live hand's one-ended rows, for the history drawer. §4.6, §13.1 */
export const useHandAdjustments = (): Adjustment[] =>
  useSessionStore((s) => s.handAdjustments);
